/**
 * 微服务发现和数据抓取页面
 * 用于从其他微服务获取信息和数据
 */

import React, { useState, useMemo } from 'react';
import { Layout } from 'antd';
import {
  Space,
  Button,
  Tag,
  Descriptions,
  Input,
  Select,
  message,
  Typography,
  Divider,
  Tree,
  Spin,
} from 'antd';
import {
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ApiOutlined,
  ClearOutlined,
  CloudServerOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { ContainerPanel } from '@ms/shared-components';
import { useServiceDiscovery } from './hooks/useServiceDiscovery';
import type { ServiceInfo } from '@ms/shared-auth';
import type { ServiceDataFetchOptions } from '../../services/serviceDiscoveryService';
import type { DataNode } from 'antd/es/tree';

const { Sider, Content } = Layout;
const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const ServiceDiscoveryPage: React.FC = () => {
  const {
    services,
    loading,
    selectedService,
    setSelectedService,
    fetchResult,
    fetching,
    healthCheckLoading,
    loadServices,
    handleSelectService,
    handleCheckHealth,
    handleFetchData,
    handleClearCache,
  } = useServiceDiscovery();

  // 数据抓取表单状态
  const [fetchForm, setFetchForm] = useState<ServiceDataFetchOptions>({
    serviceName: '',
    endpoint: '',
    method: 'GET',
    params: {},
    body: '',
    headers: {},
  });

  // 树形数据
  const treeData = useMemo(() => {
    return services.map((service) => ({
      title: (
        <Space>
          <CloudServerOutlined />
          <Text strong>{service.displayName}</Text>
          {service.status === 'online' ? (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              在线
            </Tag>
          ) : (
            <Tag color="red" icon={<CloseCircleOutlined />}>
              离线
            </Tag>
          )}
          {service.healthStatus && (
            <Tag color="green">健康</Tag>
          )}
        </Space>
      ),
      key: service.serviceName,
      icon: <GlobalOutlined />,
      children: [
        {
          title: (
            <Space direction="vertical" size={0} style={{ fontSize: 12 }}>
              <Text type="secondary">前端: {service.frontendUrl || '-'}</Text>
              <Text type="secondary">后端: {service.backendUrl || '-'}</Text>
            </Space>
          ),
          key: `${service.serviceName}-info`,
          isLeaf: true,
        },
      ],
      service: service,
    } as DataNode & { service: ServiceInfo }));
  }, [services]);

  // 树节点选择处理
  const handleTreeSelect = (selectedKeys: React.Key[], info: any) => {
    const node = info.node as DataNode & { service?: ServiceInfo };
    if (node.service) {
      const service = node.service;
      handleSelectService(service);
      // 自动填充到表单
      setFetchForm((prev) => ({
        ...prev,
        serviceName: service.serviceName,
      }));
    }
  };


  // 处理数据抓取表单提交
  const handleFetchSubmit = () => {
    if (!fetchForm.serviceName) {
      message.warning('请选择微服务');
      return;
    }
    if (!fetchForm.endpoint) {
      message.warning('请输入接口路径');
      return;
    }

    const options: ServiceDataFetchOptions = {
      serviceName: fetchForm.serviceName,
      endpoint: fetchForm.endpoint,
      method: fetchForm.method || 'GET',
    };

    // 解析查询参数
    if (fetchForm.params && Object.keys(fetchForm.params).length > 0) {
      options.params = fetchForm.params;
    }

    // 解析请求体
    if (fetchForm.body && (fetchForm.method === 'POST' || fetchForm.method === 'PUT')) {
      try {
        options.body = JSON.parse(fetchForm.body);
      } catch (e) {
        message.error('请求体必须是有效的 JSON 格式');
        return;
      }
    }

    // 解析请求头
    if (fetchForm.headers && Object.keys(fetchForm.headers).length > 0) {
      options.headers = fetchForm.headers;
    }

    handleFetchData(options);
  };

  // 树面板按钮
  const treeButtons = useMemo(
    () => [
      {
        key: 'refresh',
        icon: <ReloadOutlined />,
        type: 'text' as const,
        onClick: loadServices,
        title: '刷新',
        showTextOnHover: true,
      },
      {
        key: 'clearCache',
        icon: <ClearOutlined />,
        type: 'text' as const,
        onClick: handleClearCache,
        title: '清除缓存',
        showTextOnHover: true,
      },
    ],
    [loadServices, handleClearCache]
  );

  // 数据抓取面板按钮
  const fetchPanelButtons = useMemo(
    () => [
      {
        key: 'refresh',
        icon: <ReloadOutlined />,
        type: 'text' as const,
        onClick: loadServices,
        title: '刷新',
        showTextOnHover: true,
      },
    ],
    [loadServices]
  );

  return (
    <div style={{ height: '100vh', overflow: 'hidden' }}>
      <Layout style={{ height: '100%', display: 'flex', gap: 16, backgroundColor: '#fff' }}>
        {/* 左侧：微服务树 */}
        <Sider
          width={400}
          style={{
            background: '#fff',
            borderRight: '1px solid #f0f0f0',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
          }}
        >
          <ContainerPanel title="微服务列表" buttons={treeButtons} height={'100%'}>
            <Spin spinning={loading}>
              <Tree
                showIcon
                defaultExpandAll
                treeData={treeData}
                onSelect={handleTreeSelect}
                selectedKeys={selectedService ? [selectedService.serviceName] : []}
                showLine
                blockNode
              />
            </Spin>
          </ContainerPanel>
        </Sider>

        {/* 右侧：数据抓取面板 */}
        <Content
          style={{
            overflow: 'auto',
            backgroundColor: '#fff',
            height: '100%',
          }}
        >
          <ContainerPanel title="数据抓取" buttons={fetchPanelButtons} height={'100%'}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Title level={5}>选择微服务</Title>
                <Select
                  style={{ width: '100%' }}
                  placeholder="选择要抓取数据的微服务"
                  value={fetchForm.serviceName}
                  onChange={(value) =>
                    setFetchForm((prev) => ({ ...prev, serviceName: value }))
                  }
                >
                  {services.map((service) => (
                    <Option key={service.serviceName} value={service.serviceName}>
                      {service.displayName} ({service.serviceName})
                    </Option>
                  ))}
                </Select>
              </div>

              <div>
                <Title level={5}>请求配置</Title>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <div>
                    <Text>接口路径：</Text>
                    <Input
                      placeholder="/api/example"
                      value={fetchForm.endpoint}
                      onChange={(e) =>
                        setFetchForm((prev) => ({ ...prev, endpoint: e.target.value }))
                      }
                      addonBefore={
                        <Select
                          value={fetchForm.method}
                          onChange={(value) =>
                            setFetchForm((prev) => ({ ...prev, method: value }))
                          }
                          style={{ width: 100 }}
                        >
                          <Option value="GET">GET</Option>
                          <Option value="POST">POST</Option>
                          <Option value="PUT">PUT</Option>
                          <Option value="DELETE">DELETE</Option>
                        </Select>
                      }
                    />
                  </div>

                  {(fetchForm.method === 'GET' || fetchForm.method === 'DELETE') && (
                    <div>
                      <Text>查询参数（JSON 格式，例如：{"{ \"key\": \"value\" }"}）：</Text>
                      <TextArea
                        rows={3}
                        placeholder='{"key": "value"}'
                        value={
                          fetchForm.params
                            ? JSON.stringify(fetchForm.params, null, 2)
                            : ''
                        }
                        onChange={(e) => {
                          try {
                            const params = e.target.value
                              ? JSON.parse(e.target.value)
                              : {};
                            setFetchForm((prev) => ({ ...prev, params }));
                          } catch (err) {
                            // 忽略解析错误，允许用户继续输入
                          }
                        }}
                      />
                    </div>
                  )}

                  {(fetchForm.method === 'POST' || fetchForm.method === 'PUT') && (
                    <div>
                      <Text>请求体（JSON 格式）：</Text>
                      <TextArea
                        rows={6}
                        placeholder='{"key": "value"}'
                        value={fetchForm.body}
                        onChange={(e) =>
                          setFetchForm((prev) => ({ ...prev, body: e.target.value }))
                        }
                      />
                    </div>
                  )}
                </Space>
              </div>

              <Button
                type="primary"
                icon={<ApiOutlined />}
                loading={fetching}
                onClick={handleFetchSubmit}
                block
              >
                抓取数据
              </Button>

              {fetchResult && (
                <div>
                  <Divider />
                  <Title level={5}>抓取结果</Title>
                  <Descriptions bordered column={1}>
                    <Descriptions.Item label="状态">
                      <Tag color={fetchResult.success ? 'green' : 'red'}>
                        {fetchResult.success ? '成功' : '失败'}
                      </Tag>
                    </Descriptions.Item>
                    {fetchResult.statusCode && (
                      <Descriptions.Item label="HTTP 状态码">
                        {fetchResult.statusCode}
                      </Descriptions.Item>
                    )}
                    {fetchResult.duration && (
                      <Descriptions.Item label="耗时">
                        {fetchResult.duration}ms
                      </Descriptions.Item>
                    )}
                    {fetchResult.error && (
                      <Descriptions.Item label="错误信息">
                        <Text type="danger">{fetchResult.error}</Text>
                      </Descriptions.Item>
                    )}
                    {fetchResult.data && (
                      <Descriptions.Item label="响应数据">
                        <TextArea
                          rows={10}
                          value={JSON.stringify(fetchResult.data, null, 2)}
                          readOnly
                          style={{ fontFamily: 'monospace' }}
                        />
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </div>
              )}
            </Space>
          </ContainerPanel>
        </Content>
      </Layout>

      {/* 微服务详情模态框 */}
      {selectedService && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            background: '#fff',
            padding: 24,
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            maxWidth: 600,
            maxHeight: '80vh',
            overflow: 'auto',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <Title level={4} style={{ margin: 0 }}>
              微服务详情 - {selectedService.displayName}
            </Title>
            <Button type="link" onClick={() => setSelectedService(null)}>
              关闭
            </Button>
          </div>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="服务名称" span={2}>
              {selectedService.serviceName}
            </Descriptions.Item>
            <Descriptions.Item label="显示名称">
              {selectedService.displayName}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={selectedService.status === 'online' ? 'green' : 'red'}>
                {selectedService.status === 'online' ? '在线' : '离线'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="健康状态">
              <Tag color={selectedService.healthStatus ? 'green' : 'red'}>
                {selectedService.healthStatus ? '健康' : '异常'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="前端地址" span={2}>
              <Text code>{selectedService.frontendUrl || '-'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="后端地址" span={2}>
              <Text code>{selectedService.backendUrl || '-'}</Text>
            </Descriptions.Item>
          </Descriptions>
          <div style={{ marginTop: 16 }}>
            <Button
              type="primary"
              onClick={() => {
                setFetchForm((prev) => ({
                  ...prev,
                  serviceName: selectedService.serviceName,
                }));
                setSelectedService(null);
              }}
            >
              使用此服务抓取数据
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceDiscoveryPage;
